import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import TreeHeaderBarFilters from '../components/TreeHeaderBarFilters';
import translate from '../../../commonComponents/translate';
import TeamStructureUserCard from '../structure/TeamStructureUserCard';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Loader from '../../../services/loader';
import Notifications from '../../../services/notifications';
import withCSRF from '../../../commonComponents/withCSRF';
import { compose } from '../../../services/utilityFunctions';
import i18n from '../../../services/i18n';
import BinaryTreeButtons from '../structure/BinaryTreeButtons';
import { useDebouncedCallback, useModal } from '../../../services/customHooks';
import BinaryTreeModal from '../../signIn/BinaryTreeModal';
import BinaryUsersOnHold from '../components/BinaryUsersOnHold';
import { getInitialState } from '../helpers/auxiliarTreeHelper';
import UserProp from '../../../commonComponents/propTypes/userProp';
import ReactLoader from '../../../commonComponents/loader/ReactLoader';
import { RECURRENCE_INTERVAL_CUSTOM } from '../../../services/dateUtilityFunctions';

function BinaryTree(props) {
  const { t, _csrf } = props;

  // region INITIAL CONFIGS
  const { loggedUser } = useContext(LoggedUserContext);
  const { organization = {}, tenant = {} } = useContext(ConfigsContext);
  const { directSellingSettings = {} } = tenant?.settings || {};
  const {
    recurrence,
    binaryPlaceUserBehaviours = [],
    isManualPlaceUserBehaviourAllowed = false,
  } = organization?.directSelling || {};
  const initialState = getInitialState(recurrence);

  // region Binary Placement
  const [binaryPlaceUserBehaviour, setBinaryPlaceUserBehaviour] = useState(
    loggedUser?.directSelling?.binaryPlaceUserBehaviour,
  );
  const [isUpdateUserLoading, setIsUpdateUserLoading] = useState(false);

  const binaryPlaceUserBehavioursItems = useMemo(
    () =>
      binaryPlaceUserBehaviours?.map(behavior => ({
        id: behavior,
        text: t(`binaryBehavioursPlaceUser.${behavior}`),
      })),
    [t, binaryPlaceUserBehaviours],
  );

  /**
   * If the param from the organization is empty or only has the default config, we don’t display the selector option
   * This option will only appear if the user/sponsor is already in the binary tree
   */
  const showBinaryPlacement = useMemo(() => {
    const hasBinaryParent =
      !!loggedUser?.directSelling?.binaryParent ||
      loggedUser?.directSelling?.binarySide === 'top';
    const hasValidBehaviours =
      binaryPlaceUserBehaviours?.length > 0 &&
      binaryPlaceUserBehaviours.some(
        behavior => behavior !== UserProp.BINARY_PLACEMENT.MANUAL,
      );

    return hasBinaryParent && hasValidBehaviours;
  }, [loggedUser?.directSelling?.binaryParent, binaryPlaceUserBehaviours]);

  const handleUserUpdate = useCallback(
    useDebouncedCallback(async params => {
      try {
        setIsUpdateUserLoading(true);
        const { data: response } = await axios.put('/api/user/me', params, {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        });
        setBinaryPlaceUserBehaviour(
          response?.data?.directSelling?.binaryPlaceUserBehaviour,
        );
        Notifications.showNotificationSuccess(
          t('success'),
          t('binaryBehavioursPlaceUser.update.success'),
        );
      } catch (e) {
        Notifications.showErrorFromRequest(
          e,
          'binaryBehavioursPlaceUser.update.error',
        );
      } finally {
        setIsUpdateUserLoading(false);
      }
    }, 300),
    [setBinaryPlaceUserBehaviour, setIsUpdateUserLoading],
  );
  // endregion Binary Placement

  // endregion INITIAL CONFIGS

  // region LOADER
  const loader = useRef(new Loader('#treeContainers'));
  const [isLoading, setIsLoading] = useState(false);

  const handleLoading = useCallback(
    value => setIsLoading(value),
    [setIsLoading],
  );
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);
  // endregion LOADER

  // region BINARY TREE STATES
  const [usersPerLevel, setUsersPerLevel] = useState([]); // Tree state
  const [selectedUser, setSelectedUser] = useState(loggedUser); // User selected on tree
  const [selectedUserDetails, setSelectedUserDetails] = useState(''); // EYE information
  const [selectedUserPerLevel, setSelectedUserPerLevel] = useState([
    { level: 1, user: loggedUser },
  ]);
  // endregion BINARY TREE STATES

  const unselectUser = useCallback(() => {
    setSelectedUserDetails('');
  }, [setSelectedUserDetails]);

  const [totalAvailableUsers, setTotalAvailableUsers] = useState(0);
  const [selectedAvailableUser, setSelectedAvailableUser] = useState();

  // region FILTERS FOR BINARY TREE
  const [filters, setFilters] = useState(initialState);

  const handleFilters = useCallback(
    field => value => {
      switch (field) {
        case 'search':
          setFilters(prevState => ({ ...prevState, [field]: value }));
          break;
        default:
          console.log('**** FILTERS DEFAULT ****', field, value);
          break;
      }
    },
    [setFilters],
  );
  const handleClearFilters = useCallback(
    () => setFilters(initialState),
    [setFilters],
  );

  const handleDateChange = useCallback(
    (startDate, endDate, rangeType) => {
      setFilters(prevState => ({
        ...prevState,
        startDate,
        endDate,
        rangeType,
      }));
    },
    [setFilters],
  );
  // endregion FILTERS FOR BINARY TREE

  const fetchUserByTier = useCallback(
    async (userId, search = null) => {
      try {
        const params = {};
        if (filters?.rangeType === RECURRENCE_INTERVAL_CUSTOM) {
          params.startDate = filters.startDate || undefined;
          params.endDate = filters.endDate || undefined;
        }
        if (search) {
          params.search = search;
        }

        const endpoint = `/api/user/direct-selling/binary/members/${filters.rangeType}?binaryParent=${userId}`;

        const data = await axios.get(endpoint, {
          params: {
            ...params,
            populate: ['directSelling.binaryParent', 'recruitedBy'],
          },
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
          },
        });

        return data.data;
      } catch (err) {
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
        return [];
      }
    },
    [filters.endDate, filters.startDate, filters.search],
  );

  // Get the first user on initial render
  useEffect(() => {
    setSelectedAvailableUser('');
    handleLoading(true);
    fetchUserByTier('').then(async ({ data }) => {
      const hasNotFilterSearch = !filters.search || filters.search.length === 0;
      const diagram = hasNotFilterSearch
        ? await fetchUserByTier(data[0]._id, null)
        : await fetchUserByTier('', filters.search);

      let users = [];
      if (hasNotFilterSearch) {
        users = [
          { level: 1, users: [data[0]] },
          { level: 2, users: diagram.data },
        ];
      } else {
        users = [{ level: 1, users: diagram.data }];
      }
      setUsersPerLevel(users);
      handleLoading(false);
    });
  }, [
    fetchUserByTier,
    loggedUser,
    setUsersPerLevel,
    filters.startDate,
    filters.endDate,
    filters.search,
  ]);

  const handleUserCardClick = useCallback(
    async (level, user) => {
      const { data } = await fetchUserByTier(user._id);

      setSelectedUser(user);

      setUsersPerLevel(prev => [
        ...prev.slice(0, level),
        { level: level + 1, users: data || [] },
      ]);

      setSelectedUserPerLevel(prev => [
        ...prev.slice(0, level - 1),
        { level, user },
      ]);
    },
    [
      usersPerLevel,
      selectedUserPerLevel,
      selectedUser,
      filters.startDate,
      filters.endDate,
    ],
  );

  const isCardSelected = useCallback(
    (level, user) => {
      const found = selectedUserPerLevel.find(
        it => it.level === level && it.user._id === user._id,
      );
      return found != null;
    },
    [selectedUserPerLevel],
  );

  // region MODAL MANAGEMENT
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableUserListPage, setAvailableUserListPage] = useState(1);
  const [modalFilters, setModalFilters] = useState({ search: '' });
  const [binaryTreeModal, setBinaryTreeModal] = useModal({
    show: false,
    data: null,
  });
  const [usersOnHoldModal, setUsersOnHoldModal] = useModal({
    show: false,
    data: null,
  });

  const fetchAvailableUsers = useCallback(
    async (page, queryString) => {
      handleLoading(true);
      const { data } = await axios.get(
        '/table/user/direct-selling/binary/members/available',
        {
          params: {
            pagination: { perpage: 20, page },
            query: { generalSearch: queryString },
          },
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
          },
        },
      );
      handleLoading(false);
      return data;
    },
    [availableUsers, modalFilters.search, handleLoading],
  );

  useEffect(() => {
    (async () => {
      const data = await fetchAvailableUsers(1, modalFilters.search);
      setTotalAvailableUsers(data.meta.total);
      setAvailableUsers(data.data);
      handleLoading(false);
    })();
  }, [
    setTotalAvailableUsers,
    setAvailableUsers,
    handleLoading,
    modalFilters.search,
    usersPerLevel,
  ]);

  const onReachedEnd = useCallback(async () => {
    const data = await fetchAvailableUsers(
      availableUserListPage + 1,
      undefined,
    );

    if (availableUserListPage === data.meta.pages) {
      return;
    }

    setAvailableUserListPage(availableUserListPage + 1);
    setAvailableUsers(prev => [...prev, ...data.data]);
    handleLoading(false);
  }, [availableUserListPage]);

  const handleModalFilters = useCallback(
    field => value => {
      switch (field) {
        case 'search':
          setModalFilters(prevState => ({ ...prevState, [field]: value }));
          break;
        default:
          console.log('**** MODAL FILTERS DEFAULT ****', field, value);
          break;
      }
    },
    [setModalFilters],
  );

  const openBinaryTreeModal = useCallback(
    (side, user) => {
      if (!isManualPlaceUserBehaviourAllowed) {
        return;
      }
      setBinaryTreeModal({ show: !binaryTreeModal.show, data: { side, user } });
    },
    [binaryTreeModal.show, isManualPlaceUserBehaviourAllowed],
  );

  const onModalManagementShow = useCallback(
    (show, data) => {
      setUsersOnHoldModal({
        show,
        data,
      });
    },
    [setUsersOnHoldModal],
  );
  const updateAvailableUsers = useCallback(
    newData => {
      setAvailableUsers(prev => [...prev, ...newData]);
    },
    [setAvailableUsers],
  );
  // endregion MODAL MANAGEMENT

  // region TREE MANAGEMENT
  const addUserToTheTree = useCallback(
    async side => {
      if (!selectedAvailableUser || !isManualPlaceUserBehaviourAllowed) return;

      try {
        const { data } = await axios.put(
          `/api/user/direct-selling/binary/member/${selectedAvailableUser}`,
          { binaryParent: selectedUser._id, binarySide: side },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Organization',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );

        let level = 1;
        selectedUserPerLevel.forEach(u => {
          if (u.user._id === selectedUser._id) {
            level = u.level + 1;
          }
        });

        setUsersPerLevel(prev =>
          prev.map(l => {
            if (l.level === level) {
              l.users.push(data.data);
            }
            return l;
          }),
        );
      } catch (err) {
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
      }
    },
    [
      usersPerLevel,
      selectedUserPerLevel,
      binaryTreeModal,
      selectedUser,
      selectedAvailableUser,
      isManualPlaceUserBehaviourAllowed,
    ],
  );
  // endregion TREE MANAGEMENT
  return (
    <ReactLoader isLoading={isUpdateUserLoading}>
      <div className="connect-structure-container">
        <BinaryTreeModal
          t={t}
          query={modalFilters.search}
          setQuery={handleModalFilters('search')}
          onReachedEnd={onReachedEnd}
          userModal={binaryTreeModal}
          userTypeNames={{}}
          availableUsers={availableUsers}
          toggleModal={openBinaryTreeModal}
          addUserToTheTree={addUserToTheTree}
          selectedUserDetails={selectedUserDetails}
          selectedUserPerLevel={selectedUserPerLevel}
          directSellingSettings={directSellingSettings}
          selectedAvailableUser={selectedAvailableUser}
          setSelectedAvailableUser={setSelectedAvailableUser}
          binaryParent={binaryTreeModal?.data?.user}
        />
        {usersOnHoldModal.show && (
          <BinaryUsersOnHold
            filters={modalFilters}
            onFiltersChange={handleModalFilters}
            fetchAvailableUsers={fetchAvailableUsers}
            filteredAvailableUsers={availableUsers}
            onCloseModal={() => onModalManagementShow(false, null)}
            updateAvailableUsers={updateAvailableUsers}
            onReachedEnd={onReachedEnd}
          />
        )}
      </div>
      <TreeHeaderBarFilters
        onDateChange={handleDateChange}
        onFiltersChange={handleFilters}
        filters={filters}
        onClearFilters={handleClearFilters}
        section="binary"
        onOpenModal={() => onModalManagementShow(true, null)}
        totalAvailableUsers={totalAvailableUsers}
        showBinaryPlacement={showBinaryPlacement}
        binaryPlaceUserBehavioursItems={binaryPlaceUserBehavioursItems}
        binaryPlaceUserBehaviour={binaryPlaceUserBehaviour}
        onUpdateBinaryPlacement={handleUserUpdate}
        isUpdateUserLoading={isUpdateUserLoading}
      />
      <div id="treeContainers" className="row team-structure-container">
        <div
          id="connectStructureContainer"
          className="connect-structure-container connect-structure-vertical"
        >
          {usersPerLevel.map((l, idx) => (
            <div className="connect-structure-level-container" key={l.level}>
              <div className="connect-structure-level">
                <div className="connect-structure-level-title">
                  {filters?.search?.length > 0 && l.level === 1
                    ? t('shop.product.results')
                    : i18n.__n('connect.teams.structure.tier_level', l.level)}
                </div>
                <div className="connect-structure-user-list">
                  {l?.level > 1 && (
                    <BinaryTreeButtons
                      t={t}
                      idx={idx}
                      users={usersPerLevel}
                      setSelectedUser={setSelectedUser}
                      selectedUserPerLevel={selectedUserPerLevel}
                      onOpenModal={openBinaryTreeModal}
                      isLeft
                      isManualPlaceUserBehaviourAllowed={
                        isManualPlaceUserBehaviourAllowed
                      }
                    />
                  )}
                  {l.users
                    .sort((a, b) => {
                      // If a's binarySide is "left" and b's binarySide is "right", a should come before b
                      if (
                        a?.directSelling?.binarySide === 'left' &&
                        b?.directSelling?.binarySide === 'right'
                      ) {
                        return -1;
                      }
                      // If a's binarySide is "right" and b's binarySide is "left", b should come before a
                      if (
                        a?.directSelling?.binarySide === 'right' &&
                        b?.directSelling?.binarySide === 'left'
                      ) {
                        return 1;
                      }
                      // For all other cases, maintain the original order
                      return 0;
                    })
                    .map(user => (
                      <TeamStructureUserCard
                        t={t}
                        user={user}
                        level={l.level}
                        userTypeNames={{}}
                        onClickCard={handleUserCardClick}
                        key={`teamCard_${l.level}_${user._id}`}
                        selectedUserDetails={selectedUserDetails}
                        isSelected={isCardSelected(l.level, user)}
                        directSellingSettings={directSellingSettings}
                        setSelectedUserDetails={setSelectedUserDetails}
                        areDetailsVisible={selectedUserDetails === user._id}
                        isSponsorTree={false}
                        wrapperValuesClass="binaryTreeValuesContainer"
                        onUnselectUser={unselectUser}
                      />
                    ))}
                  {l.level > 1 && (
                    <BinaryTreeButtons
                      t={t}
                      idx={idx}
                      users={usersPerLevel}
                      setSelectedUser={setSelectedUser}
                      selectedUserPerLevel={selectedUserPerLevel}
                      onOpenModal={openBinaryTreeModal}
                      isLeft={false}
                      isManualPlaceUserBehaviourAllowed={
                        isManualPlaceUserBehaviourAllowed
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ReactLoader>
  );
}

BinaryTree.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default compose(translate, withCSRF)(BinaryTree);
